import { useEffect, useRef, useState } from 'react';
import { binanceStream } from './binanceStream';
import { coinbaseStream, toCoinbaseSymbol } from './coinbaseStream';

// Global throttle interval (ms). Live price updates are visually
// smoother when we don't render every single tick — TradingView
// itself batches at roughly ~500ms.
const TICK_THROTTLE_MS = 500;

/**
 * useBinanceTickers(symbols) — live 24h ticker rows keyed by symbol.
 * Returns `{last, change_pct, high, low, volume, quote_vol, ts, ...}`
 * for every requested symbol, updated ~1x/sec by Binance.US public WS.
 *
 * Late subscribers immediately get whatever is already cached so the
 * UI doesn't flash "—" while waiting for the next tick.
 */
export function useBinanceTickers(symbols) {
  const [rows, setRows] = useState(() => {
    const seed = {};
    for (const s of (symbols || [])) {
      const cached = binanceStream.cache.ticker.get(s.toUpperCase());
      if (cached) seed[s.toUpperCase()] = cached;
    }
    return seed;
  });
  const keyRef = useRef('');
  const symsRef = useRef([]);

  useEffect(() => {
    const norm = [...new Set((symbols || []).map((s) => s.toUpperCase()))];
    const key = norm.join(',');
    if (key === keyRef.current) return; // no change
    keyRef.current = key;
    symsRef.current = norm;
    if (!norm.length) { setRows({}); return; }
    const wanted = new Set(norm);
    const unsub = binanceStream.subscribe(norm, 'ticker');
    const off = binanceStream.addListener((msg) => {
      if (msg.kind !== 'ticker' || !wanted.has(msg.symbol)) return;
      setRows((prev) => ({ ...prev, [msg.symbol]: msg.row }));
    });
    return () => { off(); unsub(); };
  }, [symbols]);

  return rows;
}

/**
 * useBinanceTrade(symbol) — the latest executed-trade row for a
 * single symbol. Emits every trade tick (very high frequency for
 * BTC/ETH). Return shape: `{price, qty, isBuyerMaker, ts}`.
 */
export function useBinanceTrade(symbol) {
  const [row, setRow] = useState(() => symbol ? binanceStream.cache.trade.get(symbol.toUpperCase()) || null : null);
  useEffect(() => {
    if (!symbol) return;
    const S = symbol.toUpperCase();
    setRow(binanceStream.cache.trade.get(S) || null);
    const unsub = binanceStream.subscribe([S], 'trade');
    const off = binanceStream.addListener((msg) => {
      if (msg.kind !== 'trade' || msg.symbol !== S) return;
      setRow(msg.row);
    });
    return () => { off(); unsub(); };
  }, [symbol]);
  return row;
}

/**
 * useBinanceKline(symbol, tf) — live candle updates. Emits the
 * current-forming candle every ~1-2s so charts stay up-to-date
 * without polling. `isClosed=true` marks the moment the candle is
 * finalized.
 */
export function useBinanceKline(symbol, tf) {
  const [row, setRow] = useState(null);
  useEffect(() => {
    if (!symbol || !tf) return;
    const S = symbol.toUpperCase();
    setRow(binanceStream.cache.kline.get(`${S}|${tf}`) || null);
    const unsub = binanceStream.subscribe([S], 'kline', tf);
    const off = binanceStream.addListener((msg) => {
      if (msg.kind !== 'kline' || msg.symbol !== S || msg.tf !== tf) return;
      setRow(msg.row);
    });
    return () => { off(); unsub(); };
  }, [symbol, tf]);
  return row;
}

/**
 * useLiveTickers(symbols) — combined Binance + Coinbase feed with
 * ~500ms throttling so the UI updates at a readable cadence (TV-like)
 * rather than firing on every trade. Returns a merged map keyed by
 * Binance-style symbol. Coinbase gets the ultra-liquid USD/USDC pairs
 * for near-realtime feel; Binance covers everything else including
 * USDT and other quotes.
 */
export function useLiveTickers(symbols) {
  const [rows, setRows] = useState({});
  const pendingRef = useRef({});
  const flushTimerRef = useRef(null);
  const keyRef = useRef('');

  useEffect(() => {
    const norm = [...new Set((symbols || []).map((s) => s.toUpperCase()))];
    const key = norm.join(',');
    if (key === keyRef.current) return;
    keyRef.current = key;
    if (!norm.length) { setRows({}); return; }

    // Coinbase symbol map — falls back to `USD` for `USDT` and `USDC`
    // so users searching USDC pairs still see the ultra-live feed on
    // Coinbase-listed products (BTC-USD, ETH-USD, etc.).
    const cbToBin = {};
    const cbIds = [];
    for (const s of norm) {
      const cb = toCoinbaseSymbol(s);
      if (cb) { cbToBin[cb] = s; cbIds.push(cb); }
    }
    const wantedBin = new Set(norm);
    const wantedCb  = new Set(cbIds);

    // Seed with cached values (so late subscribers don't see '—').
    const seed = {};
    for (const s of norm) {
      const cached = binanceStream.cache.ticker.get(s);
      if (cached) seed[s] = { ...cached, source: 'binance' };
      const cb = toCoinbaseSymbol(s);
      const cbCached = cb ? coinbaseStream.cache.ticker.get(cb) : null;
      if (cbCached) {
        const prev = seed[s];
        if (!prev || (cbCached.ts || 0) > (prev.ts || 0)) {
          seed[s] = { ...cbCached, symbol: s, source: 'coinbase' };
        }
      }
    }
    setRows(seed);

    const scheduleFlush = () => {
      if (flushTimerRef.current) return;
      flushTimerRef.current = setTimeout(() => {
        const patch = pendingRef.current;
        pendingRef.current = {};
        flushTimerRef.current = null;
        if (Object.keys(patch).length) {
          setRows((prev) => ({ ...prev, ...patch }));
        }
      }, TICK_THROTTLE_MS);
    };

    const unsubBin = binanceStream.subscribe(norm, 'ticker');
    const unsubCb  = cbIds.length ? coinbaseStream.subscribe(cbIds) : (() => {});

    const offBin = binanceStream.addListener((msg) => {
      if (msg.kind !== 'ticker' || !wantedBin.has(msg.symbol)) return;
      pendingRef.current[msg.symbol] = { ...msg.row, source: 'binance' };
      scheduleFlush();
    });
    const offCb = coinbaseStream.addListener((msg) => {
      if (msg.kind !== 'ticker' || !wantedCb.has(msg.symbol)) return;
      const binSym = cbToBin[msg.symbol];
      if (!binSym) return;
      pendingRef.current[binSym] = { ...msg.row, symbol: binSym, source: 'coinbase' };
      scheduleFlush();
    });

    return () => {
      offBin(); offCb(); unsubBin(); unsubCb();
      if (flushTimerRef.current) { clearTimeout(flushTimerRef.current); flushTimerRef.current = null; }
      pendingRef.current = {};
    };
  }, [symbols]);

  return rows;
}

/**
 * useLiveTrade(symbol) — highest-frequency price feed for a single
 * symbol, throttled to ~500ms. Prefers Coinbase's ticker (fires on
 * every executed match) and falls back to Binance's trade stream.
 * For non-Coinbase quotes (BUSD, TRY, etc.), Binance's `@trade`
 * channel is the only source.
 */
export function useLiveTrade(symbol) {
  const [row, setRow] = useState(null);
  const pendingRef = useRef(null);
  const flushTimerRef = useRef(null);

  useEffect(() => {
    if (!symbol) return;
    const S  = symbol.toUpperCase();
    const CB = toCoinbaseSymbol(S);

    // Seed.
    const cbSeed = CB ? coinbaseStream.cache.ticker.get(CB) : null;
    const binSeed = binanceStream.cache.trade.get(S);
    if (cbSeed) setRow({ price: cbSeed.last, ts: cbSeed.ts, source: 'coinbase', side: cbSeed.side });
    else if (binSeed) setRow({ price: binSeed.price, ts: binSeed.ts, source: 'binance' });

    const scheduleFlush = () => {
      if (flushTimerRef.current) return;
      flushTimerRef.current = setTimeout(() => {
        const p = pendingRef.current;
        pendingRef.current = null;
        flushTimerRef.current = null;
        if (p) setRow(p);
      }, TICK_THROTTLE_MS);
    };

    const unsubBin = binanceStream.subscribe([S], 'trade');
    const unsubCb  = CB ? coinbaseStream.subscribe([CB]) : (() => {});

    const offBin = binanceStream.addListener((msg) => {
      if (msg.kind !== 'trade' || msg.symbol !== S) return;
      pendingRef.current = { price: msg.row.price, ts: msg.row.ts, source: 'binance', side: msg.row.isBuyerMaker ? 'sell' : 'buy' };
      scheduleFlush();
    });
    const offCb = coinbaseStream.addListener((msg) => {
      if (msg.kind !== 'ticker' || msg.symbol !== CB) return;
      pendingRef.current = { price: msg.row.last, ts: msg.row.ts, source: 'coinbase', side: msg.row.side };
      scheduleFlush();
    });

    return () => {
      offBin(); offCb(); unsubBin(); unsubCb();
      if (flushTimerRef.current) { clearTimeout(flushTimerRef.current); flushTimerRef.current = null; }
      pendingRef.current = null;
    };
  }, [symbol]);
  return row;
}
